import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import apiClient from '@/utils/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, Trash2, Users } from 'lucide-react';

const StudentEdit = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: '',
    dob: '',
    class_name: '',
    section: '',
    room_number: '',
    blood_group: '',
    father_name: '',
    mother_name: '',
    primary_phone: '',
    secondary_phone: '',
    parent_email: '',
    local_guardian_name: '',
    local_guardian_phone: '',
    address: '',
    interests: '',
    known_diseases: '',
    assigned_warden_id: '',
    secondary_warden_id: ''
  });
  const [wardens, setWardens] = useState([]);
  const [classes, setClasses] = useState([]);
  const [assignedWardenIds, setAssignedWardenIds] = useState([]);

  useEffect(() => {
    fetchStudent();
    fetchWardens();
    fetchClasses();
  }, [studentId]);

  const fetchStudent = async () => {
    try {
      const response = await apiClient.get(`/v1/admin/students/${studentId}`);
      const student = response.data;
      console.log('Student data:', student);
      setFormData({
        name: student.name || '',
        age: student.age?.toString() || '',
        gender: student.gender || '',
        dob: student.dob || student.date_of_birth || '',
        class_name: student.class_name || '',
        section: student.section || '',
        room_number: student.room_number || '',
        blood_group: student.blood_group || '',
        father_name: student.father_name || '',
        mother_name: student.mother_name || '',
        primary_phone: student.primary_phone || '',
        secondary_phone: student.secondary_phone || '',
        parent_email: student.parent_email || '',
        local_guardian_name: student.local_guardian_name || '',
        local_guardian_phone: student.local_guardian_phone || '',
        address: student.address || '',
        interests: Array.isArray(student.interests) ? student.interests.join(', ') : '',
        known_diseases: student.known_diseases || '',
        assigned_warden_id: student.assigned_warden_id || '',
        secondary_warden_id: student.secondary_warden_id || ''
      });

      // Populate multiple assigned wardens list
      const rawWardenIds = Array.isArray(student.assigned_warden_ids) && student.assigned_warden_ids.length > 0
        ? student.assigned_warden_ids
        : [student.assigned_warden_id, student.secondary_warden_id].filter(Boolean);
      const uniqueWardenIds = Array.from(new Set(rawWardenIds.filter(Boolean)));
      setAssignedWardenIds(uniqueWardenIds);
    } catch (error) {
      toast.error('Failed to load student details');
      // navigate('/admin/students');
    } finally {
      setLoading(false);
    }
  };

  const fetchWardens = async () => {
    try {
      const response = await apiClient.get(`/v1/admin/wardens/?hostel_id=${user.hostel_id}`);
      setWardens(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Failed to load wardens:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await apiClient.get(`/v1/admin/classes/?hostel_id=${user.hostel_id}`);
      setClasses(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Failed to load classes:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddWardenSlot = () => {
    // If all wardens are already selected, alert admin
    const selectedCount = assignedWardenIds.filter(Boolean).length;
    if (selectedCount >= wardens.length) {
      toast.error('All available wardens in this hostel are already assigned.');
      return;
    }
    setAssignedWardenIds(prev => [...prev, '']);
  };

  const handleWardenChange = (index, value) => {
    setAssignedWardenIds(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleRemoveWardenSlot = (index) => {
    setAssignedWardenIds(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    // age check 
    if (formData.age < 0) {
      toast.error('Age cannot be negative');
      setSaving(false);
      return;
    }
    // age limit
    if (formData.age > 100) {
      toast.error('Age cannot be greater than 100');
      setSaving(false);
      return;
    }

    // Clean and validate assigned wardens list
    const validWardenIds = Array.from(new Set(assignedWardenIds.filter(Boolean)));

    try {
      const payload = {
        name: formData.name,
        age: parseInt(formData.age),
        gender: formData.gender || null,
        dob: formData.dob || null,
        class_name: formData.class_name,
        section: formData.section || null,
        room_number: formData.room_number || null,
        blood_group: formData.blood_group,
        father_name: formData.father_name,
        mother_name: formData.mother_name || null,
        secondary_phone: formData.secondary_phone || null,
        parent_email: formData.parent_email,
        local_guardian_name: formData.local_guardian_name || null,
        local_guardian_phone: formData.local_guardian_phone || null,
        address: formData.address,
        interests: formData.interests.split(',').map(i => i.trim()).filter(Boolean),
        known_diseases: formData.known_diseases || null,
        assigned_warden_ids: validWardenIds,
        assigned_warden_id: validWardenIds[0] || null,
        secondary_warden_id: validWardenIds[1] || null
      };

      await apiClient.put(`/v1/admin/students/${studentId}`, payload);
      toast.success('Student updated successfully!');
      navigate('/admin/students');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update student');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/students')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Students
          </Button>
          <h1 className="text-2xl font-bold" style={{ color: '#2C5F7F' }}>
            Edit Student
          </h1>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Student Information */}
          <div>
            <h2 className="text-lg font-semibold mb-4" style={{ color: '#2C5F7F' }}>
              Student Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Student Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  placeholder="Full name"
                />
              </div>
              <div>
                <Label htmlFor="age">Age *</Label>
                <Input
                  id="age"
                  type="number"
                  value={formData.age}
                  onChange={(e) => handleChange('age', e.target.value)}
                  required
                  placeholder="Age"
                />
              </div>
              <div>
                <Label htmlFor="class_name">Class *</Label>
                <select
                  id="class_name"
                  value={formData.class_name}
                  onChange={(e) => handleChange('class_name', e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Class</option>
                  {classes.map((cls) => (
                    <option key={cls.id} value={cls.name}>
                      {cls.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="section">Section</Label>
                <Input
                  id="section"
                  value={formData.section}
                  onChange={(e) => handleChange('section', e.target.value)}
                  placeholder="e.g., A, B (optional)"
                />
              </div>
              <div>
                <Label htmlFor="room_number">Room Number</Label>
                <Input
                  id="room_number"
                  value={formData.room_number}
                  onChange={(e) => handleChange('room_number', e.target.value)}
                  placeholder="Room number (optional)"
                />
              </div>
              <div>
                <Label htmlFor="gender">Gender *</Label>
                <select
                  id="gender"
                  value={formData.gender}
                  onChange={(e) => handleChange('gender', e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <Label htmlFor="dob">Birth Date *</Label>
                <Input
                  id="dob"
                  type="date"
                  value={formData.dob}
                  onChange={(e) => handleChange('dob', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="blood_group">Blood Group *</Label>
                <select
                  id="blood_group"
                  value={formData.blood_group}
                  onChange={(e) => handleChange('blood_group', e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </select>
              </div>
            </div>
          </div>

          {/* Parent Information */}
          <div>
            <h2 className="text-lg font-semibold mb-4" style={{ color: '#2C5F7F' }}>
              Parent Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="father_name">Father Name *</Label>
                <Input
                  id="father_name"
                  value={formData.father_name}
                  onChange={(e) => handleChange('father_name', e.target.value)}
                  required
                  placeholder="Father's full name"
                />
              </div>
              <div>
                <Label htmlFor="mother_name">Mother Name</Label>
                <Input
                  id="mother_name"
                  value={formData.mother_name}
                  onChange={(e) => handleChange('mother_name', e.target.value)}
                  placeholder="Mother's full name (optional)"
                />
              </div>
              <div>
                <Label htmlFor="primary_phone">Primary Phone (Login ID) *</Label>
                <Input
                  id="primary_phone"
                  type="tel"
                  value={formData.primary_phone}
                  readOnly
                  className="bg-gray-100 cursor-not-allowed"
                  title="Cannot change primary phone (parent's login ID)"
                />
              </div>
              <div>
                <Label htmlFor="secondary_phone">Secondary Phone</Label>
                <Input
                  id="secondary_phone"
                  type="tel"
                  value={formData.secondary_phone}
                  onChange={(e) => handleChange('secondary_phone', e.target.value)}
                  placeholder="Alternate phone (optional)"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="parent_email">Parent Email *</Label>
                <Input
                  id="parent_email"
                  type="email"
                  value={formData.parent_email}
                  onChange={(e) => handleChange('parent_email', e.target.value)}
                  required
                  placeholder="parent@example.com"
                />
              </div>
            </div>
          </div>

          {/* Local Guardian Information */}
          <div>
            <h2 className="text-lg font-semibold mb-4" style={{ color: '#2C5F7F' }}>
              Local Guardian (Optional)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="local_guardian_name">Local Guardian Name</Label>
                <Input
                  id="local_guardian_name"
                  value={formData.local_guardian_name}
                  onChange={(e) => handleChange('local_guardian_name', e.target.value)}
                  placeholder="Guardian's name"
                />
              </div>
              <div>
                <Label htmlFor="local_guardian_phone">Local Guardian Phone</Label>
                <Input
                  id="local_guardian_phone"
                  type="tel"
                  value={formData.local_guardian_phone}
                  onChange={(e) => handleChange('local_guardian_phone', e.target.value)}
                  placeholder="Guardian's phone"
                />
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div>
            <h2 className="text-lg font-semibold mb-4" style={{ color: '#2C5F7F' }}>
              Additional Information
            </h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="address">Address *</Label>
                <textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  required
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Full residential address"
                />
              </div>
              <div>
                <Label htmlFor="interests">Interests</Label>
                <Input
                  id="interests"
                  value={formData.interests}
                  onChange={(e) => handleChange('interests', e.target.value)}
                  placeholder="e.g., Sports, Music, Art (optional)"
                />
              </div>
              <div>
                <Label htmlFor="known_diseases">Known Diseases/Allergies</Label>
                <Input
                  id="known_diseases"
                  value={formData.known_diseases}
                  onChange={(e) => handleChange('known_diseases', e.target.value)}
                  placeholder="Any medical conditions (optional)"
                />
              </div>
              <div className="pt-4 border-t space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#2C5F7F]" />
                    <div>
                      <Label className="text-base font-semibold text-gray-800">
                        Assigned Wardens
                      </Label>
                      <p className="text-xs text-gray-500">
                        Assign one, two, or more wardens. You can add as many wardens as needed. The first warden serves as Primary.
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddWardenSlot}
                    disabled={assignedWardenIds.filter(Boolean).length >= wardens.length}
                    className="flex items-center gap-1.5 text-blue-600 border-blue-200 hover:bg-blue-50"
                  >
                    <Plus className="w-4 h-4" />
                    Add Warden
                  </Button>
                </div>

                {assignedWardenIds.length === 0 ? (
                  <div className="p-4 border border-dashed border-gray-300 rounded-lg text-center bg-gray-50">
                    <p className="text-sm text-gray-600 mb-2">No wardens currently assigned to this student.</p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleAddWardenSlot}
                      className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Assign a Warden
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {assignedWardenIds.map((selectedWardenId, index) => {
                      const otherSelected = assignedWardenIds.filter((_, i) => i !== index);

                      return (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg transition-all"
                        >
                          <div className="w-32 flex-shrink-0">
                            <span
                              className={`text-xs font-semibold px-2.5 py-1 rounded inline-block ${
                                index === 0
                                  ? 'bg-blue-100 text-blue-800'
                                  : index === 1
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-gray-200 text-gray-800'
                              }`}
                            >
                              {index === 0 ? 'Primary Warden' : `Warden #${index + 1}`}
                            </span>
                          </div>

                          <div className="flex-1">
                            <select
                              value={selectedWardenId}
                              onChange={(e) => handleWardenChange(index, e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                            >
                              <option value="">-- Select Warden --</option>
                              {wardens.map((warden) => {
                                const isTakenElsewhere = otherSelected.includes(warden.id);
                                return (
                                  <option
                                    key={warden.id}
                                    value={warden.id}
                                    disabled={isTakenElsewhere}
                                  >
                                    {warden.name} ({warden.phone}) {isTakenElsewhere ? '(Already selected above)' : ''}
                                  </option>
                                );
                              })}
                            </select>
                          </div>

                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveWardenSlot(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2"
                            title="Remove this warden assignment"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Wardens count / summary preview */}
                {assignedWardenIds.filter(Boolean).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2 items-center">
                    <span className="text-xs font-medium text-gray-500">
                      Assigned ({assignedWardenIds.filter(Boolean).length}):
                    </span>
                    {assignedWardenIds.filter(Boolean).map((id, idx) => {
                      const w = wardens.find((item) => item.id === id);
                      if (!w) return null;
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                          {w.name} {idx === 0 ? '(Primary)' : ''}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/students')}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="btn-primary text-white"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudentEdit;
